import { ROLE_LABELS } from "../../constants";
import { RootState } from "../../reducers";
import {
  Group,
  Invitation,
  INVITATION_TYPES,
  Organization,
  TableOrganization,
  TableUser,
  User
} from "./types";

const sortUsersByEmail = (a: TableUser, b: TableUser): number => {
  if (a.email < b.email) {
    return -1;
  }

  if (a.email > b.email) {
    return 1;
  }

  return 0;
};

export const tableOrganizationsSelector = (
  state: RootState
): TableOrganization[] | null =>
  state.enterprises.organizations?.map((organization) => ({
    ...organization,
    role: ROLE_LABELS[organization.role]
  })) || null;

export const areOrganizationsLoadingSelector = (state: RootState): boolean =>
  state.enterprises.areOrganizationsLoading;

export const organizationSelector = (state: RootState): Organization | null =>
  state.enterprises.organization;

export const isOrganizationLoadingSelector = (state: RootState): boolean =>
  state.enterprises.isOrganizationLoading;

export const isOrganizationCreatingSelector = (state: RootState): boolean =>
  state.enterprises.isOrganizationCreating;

export const isOrganizationUpdatingSelector = (state: RootState): boolean =>
  state.enterprises.isOrganizationUpdating;

export const isOrganizationDeletingSelector = (state: RootState): boolean =>
  state.enterprises.isOrganizationDeleting;

export const areProjectsLoadingSelector = (state: RootState): boolean =>
  state.enterprises.areOrganizationsLoading;

export const groupsSelector = (state: RootState): Group[] | null =>
  state.enterprises.groups;

export const areGroupsLoadingSelector = (state: RootState): boolean =>
  state.enterprises.areGroupsLoading;

export const groupSelector = (state: RootState): Group | null =>
  state.enterprises.group;

export const isGroupLoadingSelector = (state: RootState): boolean =>
  state.enterprises.isGroupLoading;

export const isGroupUpdatingSelector = (state: RootState): boolean =>
  state.enterprises.isGroupUpdating;

export const tableGroupUsersSelector = (
  state: RootState
): TableUser[] | null =>
  state.enterprises.groupUsers && state.enterprises.groupInvitations
    ? [
        ...state.enterprises.groupUsers.map((user) => ({
          id: user.id,
          email: user.email,
          formattedEmail: user.email,
          role: ROLE_LABELS[user.role],
          fullName: user.full_name
        })),
        ...state.enterprises.groupInvitations.map((invitation) => ({
          id: invitation.inviteeEmail,
          email: invitation.inviteeEmail,
          formattedEmail: `${invitation.inviteeEmail} (pending invitation)`,
          invitationId: invitation.id,
          invitationType: invitation.type,
          role: "",
          fullName: ""
        }))
      ].sort(sortUsersByEmail)
    : null;

export const areGroupUsersLoadingSelector = (state: RootState): boolean =>
  state.enterprises.areGroupUsersLoading;

export const organizationUsersSelector = (state: RootState): User[] | null =>
  state.enterprises.organizationUsers;

export const tableOrganizationUsersSelector = (
  state: RootState
): TableUser[] | null =>
  state.enterprises.organizationUsers &&
  state.enterprises.organizationInvitations
    ? [
        ...state.enterprises.organizationUsers.map((user) => {
          const ownershipTransferInvitation =
            state.enterprises.organizationInvitations?.find(
              (invitation) => invitation.inviteeEmail === user.email
            );
          return {
            id: user.id,
            email: user.email,
            formattedEmail: `${user.email}${
              ownershipTransferInvitation ? " (pending ownership transfer)" : ""
            }`,
            invitationId: ownershipTransferInvitation?.id,
            invitationType: ownershipTransferInvitation?.type,
            role: ROLE_LABELS[user.role],
            fullName: user.full_name
          };
        }),
        ...state.enterprises.organizationInvitations
          .filter(
            (invitation) =>
              invitation.type === INVITATION_TYPES.ORGANIZATION_ADMINISTRATOR
          )
          .map((invitation) => ({
            id: invitation.inviteeEmail,
            email: invitation.inviteeEmail,
            formattedEmail: `${invitation.inviteeEmail} (pending invitation)`,
            invitationId: invitation.id,
            invitationType: invitation.type,
            role: "",
            fullName: ""
          }))
      ].sort(sortUsersByEmail)
    : null;

export const areOrganizationUsersLoadingSelector = (
  state: RootState
): boolean => state.enterprises.areOrganizationUsersLoading;

export const isUserRemovingFromOrganizationSelector = (
  state: RootState
): boolean => state.enterprises.isUserRemovingFromOrganization;

export const userIdToRemoveFromOrganizationSelector = (
  state: RootState
): string | null => state.enterprises.userIdToRemoveFromOrganization;

export const userRemovalFromOrganizationErrorSelector = (
  state: RootState
): string | null => state.enterprises.userRemovalFromOrganizationError;

export const isOrganizationOwnerChangingSelector = (
  state: RootState
): boolean => state.enterprises.isOrganizationOwnerChanging;

export const isUserRemovingFromGroupSelector = (state: RootState): boolean =>
  state.enterprises.isUserRemovingFromGroup;

export const isGroupCreatingSelector = (state: RootState): boolean =>
  state.enterprises.isGroupCreating;

export const isGroupDeletingSelector = (state: RootState): boolean =>
  state.enterprises.isGroupDeleting;

export const isGroupLeavingSelector = (state: RootState): boolean =>
  state.enterprises.isGroupLeaving;

export const areOrganizationInvitationsLoadingSelector = (
  state: RootState
): boolean => state.enterprises.areOrganizationInvitationsLoading;

export const isUserInvitingToOrganizationSelector = (
  state: RootState
): boolean => state.enterprises.isUserInvitingToOrganization;

export const areGroupInvitationsLoadingSelector = (state: RootState): boolean =>
  state.enterprises.areGroupInvitationsLoading;

export const isUserInvitingToGroupSelector = (state: RootState): boolean =>
  state.enterprises.isUserInvitingToGroup;

export const invitationSelector = (state: RootState): Invitation | null =>
  state.enterprises.organizationInvitation || state.enterprises.groupInvitation;

export const isInvitationLoadingSelector = (state: RootState): boolean =>
  state.enterprises.isOrganizationInvitationLoading ||
  state.enterprises.isGroupInvitationLoading;

export const isInvitationAcceptingSelector = (state: RootState): boolean =>
  state.enterprises.isOrganizationInvitationAccepting ||
  state.enterprises.isGroupInvitationAccepting;

export const isOrganizationInvitationDeletingSelector = (
  state: RootState
): boolean => state.enterprises.isOrganizationInvitationDeleting;

export const isGroupInvitationDeletingSelector = (state: RootState): boolean =>
  state.enterprises.isGroupInvitationDeleting;

export const invitationAcceptanceErrorSelector = (
  state: RootState
): string | null =>
  state.enterprises.organizationInvitationAcceptanceError ||
  state.enterprises.groupInvitationAcceptanceError;

export const isNewUserRegisteringSelector = (state: RootState): boolean =>
  state.enterprises.isNewUserRegistering;

export const isBillingAdminsListUpdatingSelector = (
  state: RootState
): boolean => state.enterprises.isBillingAdminsListUpdating;
